// Will be removed once we finish releasing web inbox
// eslint-disable-next-line no-restricted-imports
import { ROUTES_INBOX } from "@accurx/inbox/routes";

export type Route =
    | (typeof ROUTES)[keyof typeof ROUTES]
    | (typeof ROUTES_BASE)[keyof typeof ROUTES_BASE]
    | (typeof ROUTES_PRIMARY)[keyof typeof ROUTES_PRIMARY];

export const ROUTES = {
    desktopLaunch: "/desktop-launch",
    postSsoRedirect: "/post-sso-redirect",
    postlogin: "/postlogin",
    alreadyLoggedIn: "/already-logged-in",
    terms_and_conditions: "/terms-and-conditions",
    delivery_receipts: "/delivery-receipts",
    home: "/",
    joinOrganisation: "/join-organisation",
    addOrganisation: "/add-organisation",
    joinPharmacy: "/join-pharmacy",
    joinWorkspace: "/join-workspace",
    createWorkspace: "/create-workspace",
    get_started: "/get-started",
    message_practice: "/message-practice",
    message_templates: "/message-templates",
    patient_lists: "/patient-lists",
    patient_conversations: "/conversations/patient",
    conversation: "/conversation/:conversationId",
    patientsList: "/patients/lists/:patientListId",
    joinWorkspaceDesktop: "/join-workspace/:nationalCode",

    // record view
    /**
     * @deprecated Please use `ROUTES_WORKSPACE.remoteRecordView` instead
     */
    record_view_request_medical_record: "/remote-record-view",
    record_view_medical_record: "/remote-record-view/medical-record",
    record_view_medical_record_test_patient:
        "/remote-record-view/medical-record?request=example",
    record_view_medical_record_requests: "/medical-record-requests",

    send_message: "/send-message",
    two_factor_auth: "/two-factor-auth",
    video_confirmation: "/video-confirmation",
    reply_from_web: "/clinician-conversation/:urlId",
    message_gp: "/clinician-conversation",
    message_gp_test: "/clinician-conversation-test",

    // Workspace settings
    workspaceInvitation: "/workspace-invitation",

    //custom florey imports
    importChooseOrg: "/import/:shareId",
    confirmChangeOrg: "/import/:shareId/switching-org",

    inbox: "/inbox/w/:workspaceId",
    ambientScribe: "/w/:workspaceId/ambient-scribe",
} as const;

/**
 * Routes that only users who are not signed in should be able to access.
 */
export const UNAUTHED_ROUTES = {
    register: "/register",
    confirmEmailVerify: "/confirm-email-verify",
    downloadDesktop: "/download-desktop",
    forgotPassword: "/forgot-password",
    resetPassword: "/reset-password",
    resetPasswordConfirm: "/reset-password-confirm",
    login: "/login",
    loginOrganisations: "/login/organisations",
    loginRecordView: "/login/record-view",
    loginActiveDirectory: "/login/:trustId(uhl)",
} as const;

/**
 * Routes that anyone can access whether signed in or out.
 */
export const PUBLIC_ROUTES = {
    magicLink: "/magic-link", // Magic Link has its own flow to detect logged-in users
    checkYourInbox: "/check-your-inbox", // Used after Magic Link
    sso_failed: "/sso-failed",
    noOdsCode: "/no-ods-code",
    unableToLookupOds: "/unable-to-lookup-ods",
    userSignoutConfirm: "/Account/SignOutEverywhereConfirmation",
    patient_message_history: "/message-history",
};

// ROUTES_PRIMARY entries do not route to any pages and are only used for finding the base route of each path
export const ROUTES_PRIMARY = {
    patients: "/patients",
    patientsLists: "/patients/lists",
    workspaceConversations: "/w/:workspaceId/conversations",
    workspacePatientProfile: "/w/:workspaceId/:path*/patient-profile",
    workspaceSettings: "/w/:workspaceId/settings",
    account: "/account",
    workspaceBase: "/w/:workspaceId",
    // Trust wide
    batchMessageTrust: "/w/:workspaceId/batch-messages",
    // UHL specific
    batchMessages: "/batch-messages",
} as const;

// All of ROUTES_BASE entries should reflect a highlighted path in the side navigation
export const ROUTES_BASE = {
    // Navigation Secondary Patients
    patientsSearch: "/patients/search",
    integratedClinicListsAll: `${ROUTES_PRIMARY.workspaceBase}/patients/clinics`,
    integratedClinic: `${ROUTES_PRIMARY.workspaceBase}/patients/clinics/:clinicCode/:clinicDay/:location`,
    integratedClinicSelectPatients: `${ROUTES_PRIMARY.workspaceBase}/patients/clinics/:clinicCode/:clinicDay/:location/batch-messages/select-patients`,
    patientsListsAll: "/patients/lists/all",
    patientsListsId: "/patients/lists/:patientListId",

    // Navigation Secondary Account
    accountUserNameAndEmail: "/account/user/name-and-email",
    accountUserPassword: "/account/user/password",
    accountUserWorkspaceAccess: "/account/user/workspace-access",
    accountUserManageSignature: "/account/user/manage-signature",
    accountUserCookies: "/account/user/cookies",
    accountUserSecurity: "/account/user/security",
    accountUserTermsAndConditions: "/account/user/terms-and-conditions",

    // Navigation Secondary Batch Messages (UHL only)
    batchMessagesOverview: "/batch-messages/overview",
    batchMessageSend: "/batch-messages/send",
    batchMessageChoose: "/batch-messages/send/choose",
    batchMessageComposeSms: "/batch-messages/send/compose-sms",
    batchMessageComposeFlorey: "/batch-messages/send/compose-florey",
    batchMessagePatients: "/batch-messages/send/patients",
    batchMessageUpload: "/batch-messages/send/upload",
    batchMessageCopyPaste: "/batch-messages/send/copy-paste",
    batchMessagePatientList: "/batch-messages/send/patient-list",
    batchMessageSchedule: "/batch-messages/send/schedule",
    batchMessageReview: "/batch-messages/send/review",

    // Navigation Secondary (trust) batch messages
    batchMessageTrustSend: `${ROUTES_PRIMARY.batchMessageTrust}/send`,
} as const;

export const PATIENT_LISTS_ID_ROUTE = `${ROUTES_BASE.patientsListsId}(\\d+)`;

// Those routes will be appended to a route defined in ROUTES_BASE
export const ROUTES_EXTENSION = {
    messagePractice: "/message-practice",
    clinicianConversation: "/clinician-conversation",
    patientProfile: "/patient-profile",
    remoteRecordView: "/remote-record-view",
    remoteRecordViewMedicalRecord: "/remote-record-view/medical-record",
    remoteRecordViewMedicalRecordTestPatient:
        "/remote-record-view/medical-record?request=example",
    sendMessage: "/send-message",
    videoConfirmation: "/video-confirmation",
    workspaceInviteForm: "/invite",
} as const;

export const SLUGS_GP_BATCH = {
    choose: "batch-messaging-choose",
    composeCopd: "batch-messaging-compose-copd",
    composeFlorey: "batch-messaging-compose-florey",
    composeSelfbook: "batch-messaging-compose-selfbook",
    composeSms: "batch-messaging-compose-sms",
    review: "batch-messaging-review",
    upload: "batch-messaging-upload",
    schedule: "batch-messaging-schedule",
};

/**
 * Temporary list of all possible paths for old webinbox
 * we use these in our WorspaceConversationSection to redirect
 * from the old pages to the new pages
 */
export const TEMP_ROUTES_WEBINBOX_REDIRECTS = {
    mainConversationPage: {
        oldUrl: "/w/:workspaceId/conversations",
        newUrl: ROUTES_INBOX.MyInbox,
    },
    meAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/me/assigned",
        newUrl: ROUTES_INBOX.MyInbox,
    },
    conversationInMeAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/me/assigned/conversation/:conversationId",
        newUrl: ROUTES_INBOX.MyInbox,
    },

    meSentFolder: {
        oldUrl: "/w/:workspaceId/conversations/me/sent",
        newUrl: ROUTES_INBOX.MyInboxSent,
    },
    conversationInMeSentFolder: {
        oldUrl: "/w/:workspaceId/conversations/me/sent/conversation/:conversationId",
        newUrl: ROUTES_INBOX.MyInboxSent,
    },

    userSentFolder: {
        oldUrl: "/w/:workspaceId/conversations/user/:userId/sent",
        newUrl: ROUTES_INBOX.AllSent,
    },
    conversationUserSent: {
        oldUrl: "/w/:workspaceId/conversations/user/:userId/sent/conversation/:conversationId",
        newUrl: ROUTES_INBOX.AllSent,
    },
    userAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/user/:userId/assigned",
        newUrl: ROUTES_INBOX.Colleagues,
    },
    conversationInUserAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/user/:userId/assigned/conversation/:conversationId",
        newUrl: ROUTES_INBOX.Colleagues,
    },
    teamAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/team/:teamId/assigned",
        newUrl: ROUTES_INBOX.Teams,
    },
    conversationInTeamAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/team/:teamId/assigned/conversation/:conversationId",
        newUrl: ROUTES_INBOX.Teams,
    },

    allAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/all/assigned",
        newUrl: ROUTES_INBOX.All,
    },
    conversationInAllAssignedFolder: {
        oldUrl: "/w/:workspaceId/conversations/all/assigned/conversation/:conversationId",
        newUrl: ROUTES_INBOX.All,
    },
    allSentFolder: {
        oldUrl: "/w/:workspaceId/conversations/all/sent",
        newUrl: ROUTES_INBOX.AllSent,
    },
    conversationInAllSentFolder: {
        oldUrl: "/w/:workspaceId/conversations/all/sent/conversation/:conversationId",
        newUrl: ROUTES_INBOX.AllSent,
    },
    conversationWithoutAFolder: {
        oldUrl: "/w/:workspaceId/conversations/conversation/:conversationId",
        newUrl: ROUTES_INBOX.MyInbox,
    },
} as const;

export const ROUTES_WORKSPACE = {
    // Settings
    workspaceGeneralSettings: `${ROUTES_PRIMARY.workspaceSettings}/general`,
    workspaceEdit: `${ROUTES_PRIMARY.workspaceSettings}/edit-workspace`,
    workspaceUserManagement: `${ROUTES_PRIMARY.workspaceSettings}/users`,
    workspaceTemplatesOverview: `${ROUTES_PRIMARY.workspaceSettings}/templates`,
    workspaceCreateTemplate: `${ROUTES_PRIMARY.workspaceSettings}/templates/create`,
    workspaceEditTemplate: `${ROUTES_PRIMARY.workspaceSettings}/template/:templateId/:action(edit)`,
    workspaceCopyTemplate: `${ROUTES_PRIMARY.workspaceSettings}/template/:templateId/:action(copy)`,
    workspaceAppointmentReminders: `${ROUTES_PRIMARY.workspaceSettings}/appointment-reminders`,
    workspaceAppointmentRemindersManage: `${ROUTES_PRIMARY.workspaceSettings}/appointment-reminders/manage`,
    workspaceAppointmentRemindersSent: `${ROUTES_PRIMARY.workspaceSettings}/appointment-reminders/sent`,
    workspaceAppointmentRemindersChanges: `${ROUTES_PRIMARY.workspaceSettings}/appointment-reminders/changes`,
    workspaceAppointmentsUpload: `${ROUTES_PRIMARY.workspaceSettings}/appointments-manual-upload`,
    workspacePatientTriage: `${ROUTES_PRIMARY.workspaceSettings}/patient-triage`,
    workspacePatientTriageSettings: `${ROUTES_PRIMARY.workspaceSettings}/patient-triage/settings`,
    workspacePatientTriageSetup: `${ROUTES_PRIMARY.workspaceSettings}/patient-triage/setup`,
    workspaceMedicalRecords: `${ROUTES_PRIMARY.workspaceSettings}/medical-records`,
    workspaceDeliveryReceipts: `${ROUTES_PRIMARY.workspaceSettings}/delivery-receipts/:tab?`,
    workspaceBatchMessages: `${ROUTES_PRIMARY.workspaceSettings}/batch-messages`,
    workspaceResources: `${ROUTES_PRIMARY.workspaceSettings}/resources`,
    workspaceAvailableFeatures: `${ROUTES_PRIMARY.workspaceSettings}/available-features`,
    workspaceTeamNotifications: `${ROUTES_PRIMARY.workspaceSettings}/team-notifications`,
    workspaceSmsSettings: `${ROUTES_PRIMARY.workspaceSettings}/sms-settings`,
    workspaceInstalledComputers: `${ROUTES_PRIMARY.workspaceSettings}/installed-computers`,
    workspaceQuestionnaireBuilder: `${ROUTES_PRIMARY.workspaceSettings}/questionnaire-builder`,

    // Reporting
    reporting: `${ROUTES_PRIMARY.workspaceSettings}/reporting`,
    reportingPatientTriage: `${ROUTES_PRIMARY.workspaceSettings}/reporting/patient-triage/:tab?`,
    reportingResponseRates: `${ROUTES_PRIMARY.workspaceSettings}/reporting/response-rates/:tab?`,
    reportingFragments: `${ROUTES_PRIMARY.workspaceSettings}/reporting/fragments/:tab?`,
    reportingTeamUsage: `${ROUTES_PRIMARY.workspaceSettings}/reporting/team`,

    // AccuBook
    accubook: `${ROUTES_PRIMARY.workspaceSettings}/accubook/:tab?`,
    accubookManagePatients: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients`,
    accubookInvitesUpload: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/upload`,
    accubookInvitesUploadSecond: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/upload-second`,
    accubookInvitesSelect: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/select`,
    accubookInvitesComposeFirst: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/compose-first`,
    accubookInvitesComposeSecond: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/compose-second`,
    accubookInvitesReview: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/review`,
    accubookInvitesSingle: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/invite-single`,
    accubookSendReminder: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/send-reminder`,
    accubookCancelAppointment: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-patients/cancel-appointment`,
    accubookManageClinics: `${ROUTES_PRIMARY.workspaceSettings}/accubook/manage-clinics`,
    accubookBookedPatients: `${ROUTES_PRIMARY.workspaceSettings}/accubook/booked-patients`,
    accubookBookedPatientsSession: `${ROUTES_PRIMARY.workspaceSettings}/accubook/booked-patients/:sessionId`,
    accubookDeliverySites: `${ROUTES_PRIMARY.workspaceSettings}/accubook/delivery-sites`,
    accubookDashboard: `${ROUTES_PRIMARY.workspaceSettings}/accubook/dashboard`,

    // Conversations
    conversationsMe:
        "/w/:workspaceId/conversations/me/:relationship(assigned|sent)",
    conversationsUser:
        "/w/:workspaceId/conversations/user/:userId/:relationship(assigned|sent)",
    conversationsTeam: "/w/:workspaceId/conversations/team/:teamId/assigned",
    conversationsAll:
        "/w/:workspaceId/conversations/all/:relationship(assigned|sent)",

    // A single conversation can exist in any kind of group
    // We don't want to be prescriptive over the shape of the group path so
    // We use "/:path*" here to say that anything ending with
    // "/conversation/:conversationId" is a single conversation page.
    //
    // Some examples may be:
    // "/w/88/conversations/user/12/conversation/2572345"
    // "/w/88/conversations/search/conversation/2572345"
    // "/w/88/conversations/conversation/2572345"
    conversation:
        "/w/:workspaceId/conversations/:path*/conversation/:conversationId",

    // Searched patient
    patientProfile: "/w/:workspaceId/:path*/patient-profile/:selectedTab?",
    searchedPatientConversationNew:
        "/w/:workspaceId/:path*/patient-profile/:selectedTab?/conversation/new",
    searchedPatientConversationNewWithId:
        "/w/:workspaceId/:path*/patient-profile/:selectedTab?/conversation/new/:conversationId",
    searchedPatientConversation:
        "/w/:workspaceId/:path*/patient-profile/:selectedTab?/conversation/:conversationId",

    remoteRecordView: `${ROUTES_PRIMARY.workspaceBase}/:path*${ROUTES_EXTENSION.remoteRecordView}`,

    // Compose batch trust web user (non-UHL specific)
    batchMessageTrustSentMessages: `${ROUTES_PRIMARY.batchMessageTrust}/sent`,
    batchMessageTrustUpload: `${ROUTES_BASE.batchMessageTrustSend}/upload`,
    batchMessageTrustUploadViaFile: `${ROUTES_BASE.batchMessageTrustSend}/upload/file`,
    batchMessageTrustUploadImportPatientList: `${ROUTES_BASE.batchMessageTrustSend}/upload/import-patient-list`,
    batchMessageTrustUploadViaCopyPaste: `${ROUTES_BASE.batchMessageTrustSend}/upload/copy-paste`,
    batchMessageTrustReview: `${ROUTES_BASE.batchMessageTrustSend}/review/:batchId`,
    batchMessageTrustCompose: `${ROUTES_BASE.batchMessageTrustSend}/compose/:batchId`,
    batchMessageTrustConfirm: `${ROUTES_BASE.batchMessageTrustSend}/confirm/:batchId`,

    // GP Batch
    batchMessageGpChoose: `${ROUTES_PRIMARY.batchMessageTrust}/${SLUGS_GP_BATCH.choose}`,

    // VOIP
    voip: `/w/:workspaceId/voip`,

    // Mapping Workspaces
    workspaceMapWorkspaceToOrganisation: `${ROUTES_PRIMARY.workspaceBase}/map-workspace`,
} as const;

export const ROUTES_API = {
    downloadDesktop: "/api/desktop/download/chain",
};
